import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { defineModuleUi, useOrg } from "@vixio/data";
import { Chart, Meter, Skeleton, formatMoney, formatMoneyCompact, formatMonth, formatPct, lazyPage, monthStartIso, todayIso } from "@vixio/ui";
import { useFounderTarget, useRevenueMonthly } from "./widget-data";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const RevenuePage = lazyPage(() => import("./RevenuePage"), "RevenuePage");

function RevenueWidget() {
  const org = useOrg();
  const m = useRevenueMonthly(org.id).data?.find((x) => x.month === monthStartIso(todayIso()));
  const t = useFounderTarget(org.id).data;
  return (
    <div className="vx-stack">
      <dl className="vx-dl">
        <dt>Contratada</dt>
        <dd className="num">{formatMoney(m?.contracted_cents ?? 0)}</dd>
        <dt>Faturada</dt>
        <dd className="num">{formatMoney(m?.invoiced_cents ?? 0)}</dd>
        <dt>Recebida</dt>
        <dd className="num">{formatMoney(m?.received_cents ?? 0)}</dd>
      </dl>
      {t?.required_revenue_cents ? (
        <div>
          <Meter pct={Number(t.pct_of_required ?? 0)} tone={Number(t.pct_of_required) < 100 ? "warn" : undefined} />
          <p className="text-2" style={{ marginTop: 4, fontSize: "var(--fs-12)" }}>
            {formatPct(Number(t.pct_of_required ?? 0))} da meta de {formatMoney(t.required_revenue_cents)}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Gráfico do Início: receita contratada, faturada e recebida nos últimos 12 meses. */
function RevenueTrendWidget() {
  const org = useOrg();
  const q = useRevenueMonthly(org.id);
  if (q.isLoading) return <Skeleton lines={5} />;
  const last12 = (q.data ?? []).slice(-12);
  if (last12.length === 0) {
    return (
      <p className="muted">
        Sem receita registrada ainda. <Link to="/dinheiro/receita">Abrir receita</Link>
      </p>
    );
  }
  return (
    <Chart
      type="bar"
      title="Receita contratada, faturada e recebida nos últimos 12 meses"
      labels={last12.map((m) => formatMonth(m.month))}
      series={[
        { name: "Contratada", values: last12.map((m) => m.contracted_cents) },
        { name: "Faturada", values: last12.map((m) => m.invoiced_cents) },
        { name: "Recebida", values: last12.map((m) => m.received_cents) },
      ]}
      format={(v) => formatMoney(v)}
      formatAxis={formatMoneyCompact}
      height={220}
      aside={<Link to="/dinheiro/receita" className="vx-link-sm">Abrir receita</Link>}
    />
  );
}

export default defineModuleUi({
  key: "revenue",
  title: "Receita",
  domain: "money",
  icon: TrendingUp,
  menu: [{ path: "/dinheiro/receita", label: "Receita", minRole: "manager", icon: TrendingUp, order: 80, keywords: "contratada faturada recebida concentração meta fundador" }],
  routes: [{ path: "/dinheiro/receita", element: <RevenuePage />, minRole: "manager" }],
  widgets: [
    { key: "revenue.trend", title: "Receita nos últimos 12 meses", minRole: "manager", span: 2, order: 19, element: RevenueTrendWidget },
    { key: "revenue.month", title: "Receita do mês", minRole: "manager", span: 1, order: 25, element: RevenueWidget },
  ],
});
